// First-assignment and last-transfer times for the Tracking History tab.
//
// lead.assignedDate is overwritten with "now" on every transfer (Edit Lead and the queue
// transfer endpoints), so on its own it shows the latest hand-over. The first assignment
// comes from assignmentMetadata.assignedAt (set when the lead is assigned on arrival or
// recovered from Review Leads); then createdAt, when the lead already had an agent before its
// first transfer (e.g. an inbound call lead); then the earliest recorded transfer (a queued
// lead first assigned by an admin); then assignedDate itself.
//
// Both times are formatted in US Pacific, like the page's Created date, whatever timezone
// the PC is set to.
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const PACIFIC_TZ = "America/Los_Angeles";

// assignedDate and the matching transfer entry are written a few milliseconds apart, so a
// transfer only counts when it is more than a second away from the first assignment.
const SAME_MOMENT_MS = 1000;

const toInstant = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const instant = dayjs.utc(value);
  return instant.isValid() ? instant : null;
};

// Every recorded transfer with its transferredAt, oldest first. Both arrays are read:
// transferHistory (Edit Lead and single queue transfers) and assignmentMetadata.transferHistory.
const getTransfers = (lead) =>
  [
    ...(Array.isArray(lead?.transferHistory) ? lead.transferHistory : []),
    ...(Array.isArray(lead?.assignmentMetadata?.transferHistory) ? lead.assignmentMetadata.transferHistory : []),
  ]
    .map((entry) => ({ entry, at: toInstant(entry?.transferredAt) }))
    .filter(({ at }) => at)
    .sort((a, b) => a.at.valueOf() - b.at.valueOf());

// Only the two queue paths of assignLead set queuedAt, and transfers never clear it. A transfer
// out of the queue can still name a fromAgent (the customer's previous agent, taken from
// preferredAgentId / previousAgentId), so fromAgent alone does not prove an earlier assignment.
export const wasQueuedOnArrival = (lead) => Boolean(lead?.queuedAt || lead?.assignmentMetadata?.queuedAt);

// { firstAssignedAt, lastTransferAt } as ISO strings; lastTransferAt is null unless a transfer
// happened after the first assignment.
export const getAssignmentTimes = (lead) => {
  const transfers = getTransfers(lead);
  const oldestTransfer = transfers[0];
  const assignedDate = toInstant(lead?.assignedDate);

  // The oldest transfer came from an agent and the lead was not queued on arrival: it already
  // had that agent when it was created (e.g. an inbound call lead).
  const hadAgentFromCreation = Boolean(oldestTransfer?.entry?.fromAgent) && !wasQueuedOnArrival(lead);

  const firstAssigned =
    toInstant(lead?.assignmentMetadata?.assignedAt) ||
    (hadAgentFromCreation ? toInstant(lead?.createdAt) : null) ||
    oldestTransfer?.at ||
    assignedDate;

  if (!firstAssigned) {
    return { firstAssignedAt: null, lastTransferAt: null };
  }

  let lastTransfer = transfers.length > 0 ? transfers[transfers.length - 1].at : null;
  if (!lastTransfer && assignedDate && Math.abs(assignedDate.diff(firstAssigned)) > SAME_MOMENT_MS) {
    lastTransfer = assignedDate;
  }

  const showLastTransfer = Boolean(lastTransfer) && Math.abs(lastTransfer.diff(firstAssigned)) > SAME_MOMENT_MS;

  return {
    firstAssignedAt: firstAssigned.toISOString(),
    lastTransferAt: showLastTransfer ? lastTransfer.toISOString() : null,
  };
};

export const formatPacificDateTime = (value) => {
  const instant = toInstant(value);
  return instant ? instant.tz(PACIFIC_TZ).format("MM-DD-YYYY hh:mm:ss A") : null;
};
